import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import DOMPurify from 'dompurify';

import firebaseConfig from './firebaseConfig';
import { API_BASE_URL } from './config';

// Firebase Client SDK Initialization
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

// --- Navigation Icons ---
const HomeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const PresentationIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2"><path d="M2 3h20"/><path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3"/><path d="m7 21 5-5 5 5"/></svg>;
const TestimonialIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
const ResourceIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>;
const BulletinIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2"><path d="M11 6a13 13 0 0 0 8.4-2.8A1 1 0 0 1 21 4v12a1 1 0 0 1-1.6.8A13 13 0 0 0 11 14H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z"/><path d="M6 14a12 12 0 0 0 2.4 7.2 2 2 0 0 0 3.2-2.4A8 8 0 0 1 10 14"/><path d="M8 6v8"/></svg>;
const AdminIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 mr-2"><path d="M12 3a7 7 0 0 0-7 7v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a7 7 0 0 0-7-7z"/><path d="M12 3v18"/><path d="M12 3L7 7"/><path d="M12 3L17 7"/></svg>;
const MicIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-mic"><path d="M12 19v3"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><rect x="9" y="2" width="6" height="13" rx="3"/></svg>;

// --- Helper Functions ---
const UNKNOWN_PRESENTER = {
  id: 'unknown-presenter',
  name: 'Unknown Presenter',
  bio: '',
  image: 'https://placehold.co/100x100/cccccc/ffffff?text=??',
};


const escapeHtml = (value = '') => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const applyInlineMarkdown = (line = '') => escapeHtml(line)
  .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  .replace(/__([^_]+)__/g, '<strong>$1</strong>')
  .replace(/\*([^*]+)\*/g, '<em>$1</em>');

const normalizeGeminiTextForDisplay = (text = '') => String(text)
  .replace(/\r\n/g, '\n')
  .replace(/\s+(?=\d+\.\s+)/g, '\n')
  .replace(/\s+(?=[•]\s+)/g, '\n')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

const formatChatMessageHtml = (text = '') => {
  const normalized = normalizeGeminiTextForDisplay(text);
  if (!normalized) return '';

  const lines = normalized.split('\n').map((line) => line.trim()).filter(Boolean);
  let html = '';
  let listType = null;

  const closeList = () => {
    if (listType) {
      html += `</${listType}>`;
      listType = null;
    }
  };

  const openList = (type) => {
    if (listType === type) return;
    closeList();
    html += `<${type}>`;
    listType = type;
  };

  lines.forEach((line) => {
    const orderedMatch = line.match(/^\d+\.\s+(.+)$/);
    const unorderedMatch = line.match(/^[-•]\s+(.+)$/);

    if (orderedMatch) {
      openList('ol');
      html += `<li>${applyInlineMarkdown(orderedMatch[1])}</li>`;
      return;
    }

    if (unorderedMatch) {
      openList('ul');
      html += `<li>${applyInlineMarkdown(unorderedMatch[1])}</li>`;
      return;
    }

    closeList();
    html += `<p>${applyInlineMarkdown(line)}</p>`;
  });

  closeList();

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ol', 'ul', 'li'],
    ALLOWED_ATTR: [],
  });
};

const FormattedChatMessage = ({ text }) => (
  <div
    className="chat-message-content"
    dangerouslySetInnerHTML={{ __html: formatChatMessageHtml(text) }}
  />
);

const normalizeName = (name = '') => name.trim().toLowerCase().replace(/\s+/g, ' ');

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const generatePresenterImage = (name = '', imageUrl = '') => {
  if (imageUrl && imageUrl.trim() !== '') return imageUrl;
  return `https://placehold.co/100x100/A78BFA/ffffff?text=${encodeURIComponent(getInitials(name))}`;
};

const getGoogleDriveThumbnailUrl = (url) => {
  if (url && url.includes('drive.google.com/file/d/')) {
    const fileId = url.split('/d/')[1]?.split('/')[0];
    if (fileId) return `https://lh3.googleusercontent.com/d/${fileId}=w1200`;
  }
  return url;
};

const getGoogleDriveDirectImageUrl = (url) => {
  if (url && url.includes('drive.google.com/file/d/')) {
    const fileId = url.split('/d/')[1]?.split('/')[0];
    if (fileId) return `https://lh3.googleusercontent.com/d/${fileId}`;
  }
  return url;
};

const generateDefaultThumbnail = (link = '', thumbnail = '') => {
  if (thumbnail && thumbnail.trim() !== '') return thumbnail;
  if (!link || link.trim() === '') return 'https://placehold.co/150x100/607D8B/FFFFFF?text=Resource';

  const lowerCaseLink = link.toLowerCase();
  if (
    lowerCaseLink.includes('youtube.com') ||
    lowerCaseLink.includes('youtu.be') ||
    lowerCaseLink.includes('vimeo.com') ||
    lowerCaseLink.endsWith('.mp4') ||
    lowerCaseLink.includes('drive.google.com')
  ) {
    return 'https://placehold.co/150x100/FF0000/FFFFFF?text=Video';
  }

  if (
    lowerCaseLink.endsWith('.png') ||
    lowerCaseLink.endsWith('.jpg') ||
    lowerCaseLink.endsWith('.jpeg') ||
    lowerCaseLink.endsWith('.gif') ||
    lowerCaseLink.endsWith('.svg') ||
    lowerCaseLink.endsWith('.webp')
  ) {
    return 'https://placehold.co/150x100/4CAF50/FFFFFF?text=Image';
  }

  if (lowerCaseLink.endsWith('.wav') || lowerCaseLink.endsWith('.mp3')) {
    return 'https://placehold.co/150x100/9C27B0/FFFFFF?text=Audio';
  }

  if (
    lowerCaseLink.includes('docs.google.com/presentation') ||
    lowerCaseLink.includes('canva.com/design') ||
    lowerCaseLink.includes('slides.com')
  ) {
    return 'https://placehold.co/150x100/FFC107/000000?text=Slides';
  }

  return 'https://placehold.co/150x100/607D8B/FFFFFF?text=Resource';
};

const resolvePresenterForPresentation = (presentation, presenters) => {
  if (!presentation) return UNKNOWN_PRESENTER;

  if (presentation.presenterId) {
    const linkedPresenter = presenters.find((presenter) => presenter.id === presentation.presenterId);
    if (linkedPresenter) {
      return {
        ...linkedPresenter,
        image: generatePresenterImage(linkedPresenter.name, linkedPresenter.image),
      };
    }
  }

  if (presentation.presenter) {
    return {
      ...presentation.presenter,
      id: presentation.presenterId || `legacy-${normalizeName(presentation.presenter.name || presentation.id || '')}`,
      image: generatePresenterImage(presentation.presenter.name, presentation.presenter.image),
    };
  }

  return UNKNOWN_PRESENTER;
};

const hasOwnField = (object, fieldName) => Object.prototype.hasOwnProperty.call(object || {}, fieldName);

const getPresentationOverview = (presentation) => {
  const overview = presentation?.description || presentation?.summary || '';
  return String(overview).trim();
};

const getSpotlightStoryText = (presentation) => {
  if (!presentation) return '';

  // New records can intentionally store an empty spotlightStory to mark the story as unavailable.
  // Legacy records did not have spotlightStory, so their existing description remains the fallback story.
  if (hasOwnField(presentation, 'spotlightStory')) {
    return String(presentation.spotlightStory || '').trim();
  }

  return getPresentationOverview(presentation);
};

const hasSpotlightStory = (presentation) => getSpotlightStoryText(presentation).length > 0;

const getPresentationCardSummary = (presentation) => {
  if (!presentation) return '';

  const explicitSummary = presentation.cardSummary || presentation.summary || presentation.shortDescription;
  if (explicitSummary) {
    return String(explicitSummary).trim();
  }

  // Legacy presentation records often stored the full spotlight story in `description`.
  // Do not show that long story on the main card. It stays available through the Spotlight Story button.
  if (!hasOwnField(presentation, 'spotlightStory')) {
    return '';
  }

  return getPresentationOverview(presentation);
};

const getApiError = async (response, fallbackMessage) => {
  try {
    const data = await response.json();
    return data.error || data.message || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

// Mock fallback data
const initialPresenters = [
  {
    id: 'presenter-emily-chen',
    name: 'Dr. Emily Chen',
    bio: 'AI Ethicist and Healthcare Innovator',
    image: 'https://placehold.co/100x100/A78BFA/ffffff?text=EC',
  },
  {
    id: 'presenter-mark-johnson',
    name: 'Mark Johnson',
    bio: 'Urban Development Consultant',
    image: 'https://placehold.co/100x100/FDBA74/ffffff?text=MJ',
  },
  {
    id: 'presenter-sarah-lee',
    name: 'Sarah Lee',
    bio: 'Decentralized Tech Advocate',
    image: 'https://placehold.co/100x100/6EE7B7/ffffff?text=SL',
  },
];

const initialPresentations = [
  {
    id: '1',
    presenterId: 'presenter-emily-chen',
    title: 'The Future of AI in Healthcare',
    slidesLink: '#',
    description: 'An insightful look into how artificial intelligence is revolutionizing healthcare diagnostics and patient care. This presentation covers the ethical implications, technological advancements, and future prospects of AI in various medical fields, including diagnostics, drug discovery, and personalized treatment plans.',
  },
  {
    id: '2',
    presenterId: 'presenter-mark-johnson',
    title: 'Sustainable Urban Planning',
    slidesLink: '#',
    description: 'Exploring innovative strategies for creating eco-friendly and resilient cities for future generations. This session delves into green infrastructure, smart city technologies, public transportation solutions, and community engagement models that promote sustainability and improve urban living quality.',
  },
  {
    id: '3',
    presenterId: 'presenter-sarah-lee',
    title: 'Blockchain Beyond Cryptocurrency',
    slidesLink: '#',
    description: 'Uncovering the diverse applications of blockchain technology in supply chain, voting, and digital identity. This presentation goes beyond Bitcoin to explore how distributed ledger technology can enhance transparency, security, and efficiency across various industries, including logistics, healthcare, and finance.',
  },
];

const initialTestimonials = [
  { id: 't1', quote: 'The Prisma Archives provide an invaluable resource for anyone interested in cutting-edge ideas. Highly recommended!', author: 'Alex Rivera', role: 'Tech Enthusiast' },
  { id: 't2', quote: 'I found the presentations incredibly inspiring and well-organized. A fantastic platform for knowledge sharing.', author: 'Maria Garcia', role: 'Educator' },
  { id: 't3', quote: 'The insights from these sessions are truly transformative. A must-visit for innovators!', author: 'David Kim', role: 'Researcher' },
  { id: 't4', quote: 'Prisma Archives is an excellent hub for staying updated on the latest trends and groundbreaking ideas.', author: 'Jessica Brown', role: 'Business Analyst' },
];

const initialResources = [
  { id: 'r1', name: 'AI Ethics Whitepaper', link: '#', description: 'A comprehensive whitepaper on ethical considerations in AI development.', thumbnail: 'https://placehold.co/150x100/ADD8E6/000000?text=AI+Ethics' },
  { id: 'r2', name: 'Urban Sustainability Guide', link: '#', description: 'Practical guide for implementing sustainable practices in urban environments.', thumbnail: 'https://placehold.co/150x100/90EE90/000000?text=Urban+Guide' },
  { id: 'r3', name: 'Blockchain Explained', link: '#', description: 'An introductory guide to understanding blockchain technology.', thumbnail: 'https://placehold.co/150x100/FFD700/000000?text=Blockchain' },
];

const initialBulletins = [
  { id: 'b1', what: 'Company Holiday Party', when: 'December 24, 2024', where: 'Grand Ballroom, City Hotel', who: 'HR Department', image: 'https://placehold.co/600x400/FFC0CB/000000?text=Holiday+Party', displayUntil: '2024-12-25' },
  { id: 'b2', what: 'New Employee Onboarding Session', when: 'January 10, 2025', where: 'Main Office, Conference Room A', who: 'Training Team', image: 'https://placehold.co/600x400/ADD8E6/000000?text=Onboarding+Session', displayUntil: '2025-01-11' },
];

// Prisma Chatbot Component
const PrismaChatbot = ({ isOpen, onClose }) => {
  const [chatHistory, setChatHistory] = useState([
    { role: 'model', text: "Hello! I'm Prisma, your emotional support AI. How are you feeling today?" },
  ]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showEmoticons, setShowEmoticons] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const chatContainerRef = useRef(null);
  const recognitionRef = useRef(null);

  const SpeechRecognition = typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const sendMessage = async (messageToSend = userInput) => {
    if (!messageToSend || messageToSend.trim() === '') return;

    const userMessage = DOMPurify.sanitize(messageToSend);
    setChatHistory((prev) => [...prev, { role: 'user', text: userMessage }]);
    setUserInput('');
    setIsLoading(true);
    setShowEmoticons(false);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('You must be logged in to chat.');

      const idToken = await user.getIdToken();
      const response = await fetch(`${API_BASE_URL}/gemini-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ message: userMessage }),
      });

      const result = await response.json();
      if (response.ok && result.response) {
        setChatHistory((prev) => [...prev, { role: 'model', text: result.response }]);
      } else {
        console.error('Backend error:', result.error || 'Unknown error');
        setChatHistory((prev) => [...prev, { role: 'model', text: 'Sorry, I could not get a response.' }]);
      }
    } catch (error) {
      console.error('Error communicating with backend:', error);
      setChatHistory((prev) => [...prev, { role: 'model', text: 'An error occurred while connecting to the AI service.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setUserInput(transcript);
      setIsRecording(false);
      sendMessage(transcript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsRecording(false);
      setChatHistory((prev) => [...prev, { role: 'model', text: "Sorry, I couldn't understand your audio. Please try typing." }]);
    };

    recognition.onend = () => setIsRecording(false);
    recognitionRef.current = recognition;

    return () => {
      try {
        recognition.stop();
      } catch {
        // Ignore browser-specific stop errors.
      }
      recognitionRef.current = null;
    };
  }, [SpeechRecognition]);

  const emoticons = [
    { emoji: '😊', description: 'Happy' },
    { emoji: '😔', description: 'Sad' },
    { emoji: '😠', description: 'Angry' },
    { emoji: '😟', description: 'Anxious' },
    { emoji: '😐', description: 'Neutral' },
  ];

  const handleEmoticonClick = (description) => {
    setUserInput(description);
    setShowEmoticons(false);
    sendMessage(description);
  };

  const startRecording = () => {
    const consent = window.confirm('This feature uses your microphone to transcribe speech. Do you consent to this?');
    if (!consent) return;

    if (recognitionRef.current && !isRecording) {
      setIsRecording(true);
      setShowEmoticons(false);
      setUserInput('');
      recognitionRef.current.start();
      setChatHistory((prev) => [...prev, { role: 'model', text: '*Listening...*' }]);
    } else if (!recognitionRef.current) {
      setChatHistory((prev) => [...prev, { role: 'model', text: 'Speech recognition is not supported in your browser.' }]);
    }
  };

  const stopRecording = () => {
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md h-[80vh] flex flex-col overflow-hidden">
        <div className="p-4 bg-gradient-to-r from-purple-600 to-pink-500 text-white flex justify-between items-center rounded-t-2xl">
          <h3 className="text-xl font-bold">✨ Talk to Prisma AI</h3>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl font-bold">×</button>
        </div>
        <div ref={chatContainerRef} className="flex-grow p-4 overflow-y-auto space-y-4">
          {chatHistory.map((msg, index) => (
            <div key={`${msg.role}-${index}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'model' ? (
                <img src="/prisma-avatar.png" alt="Prisma AI" className="w-8 h-8 rounded-full object-cover mr-2" />
              ) : (
                <img src="https://placehold.co/40x40/cccccc/ffffff?text=YOU" alt="User" className="w-8 h-8 rounded-full object-cover mr-2" />
              )}
              <div className={`max-w-[86%] p-3 rounded-xl shadow-md ${msg.role === 'user' ? 'bg-blue-500 text-white rounded-br-none whitespace-pre-wrap' : 'bg-gray-200 text-gray-800 rounded-bl-none'}`}>
                {msg.role === 'model' ? <FormattedChatMessage text={msg.text} /> : msg.text}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="max-w-[75%] p-3 rounded-xl shadow-md bg-gray-200 text-gray-800 rounded-bl-none">
                <div className="animate-pulse flex space-x-2">
                  <div className="rounded-full bg-gray-400 h-2 w-2" />
                  <div className="rounded-full bg-gray-400 h-2 w-2" />
                  <div className="rounded-full bg-gray-400 h-2 w-2" />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-gray-200 flex flex-col">
          {showEmoticons && chatHistory.length === 1 && (
            <div className="flex justify-around mb-4">
              {emoticons.map((emo) => (
                <button key={emo.description} onClick={() => handleEmoticonClick(emo.description)} title={emo.description} className="text-4xl p-2 rounded-full hover:bg-gray-200 transition-colors duration-200">
                  {emo.emoji}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center">
            {SpeechRecognition && (
              <button
                onClick={isRecording ? stopRecording : startRecording}
                className={`p-3 rounded-full mr-2 transition-colors duration-200 ${isRecording ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                title={isRecording ? 'Stop Recording' : 'Start Recording'}
                disabled={isLoading}
              >
                <MicIcon />
              </button>
            )}
            <input
              type="text"
              value={userInput}
              onChange={(e) => {
                setUserInput(e.target.value);
                setShowEmoticons(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !isLoading) sendMessage();
              }}
              placeholder="Ask Prisma anything..."
              className="flex-grow p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent mr-2"
              disabled={isLoading || isRecording}
            />
            <button onClick={() => sendMessage()} className="px-5 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-300" disabled={isLoading || isRecording}>
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SpotlightStoryPage = ({ presentation, onBack }) => {
  if (!presentation) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-600">No presentation selected for spotlight story.</p>
        <button onClick={onBack} className="mt-4 px-6 py-3 bg-blue-500 text-white font-bold rounded-full shadow-md hover:bg-blue-600 transition-all duration-300">
          Back to Presentations
        </button>
      </div>
    );
  }

  const presenter = presentation.presenter || UNKNOWN_PRESENTER;
  const spotlightStory = getSpotlightStoryText(presentation);

  return (
    <div className="p-6 md:p-10 min-h-[calc(100vh-80px)] bg-gray-50">
      <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl p-8 md:p-12">
        <button onClick={onBack} className="mb-8 px-6 py-3 bg-gray-300 text-gray-800 font-semibold rounded-full shadow-md hover:bg-gray-400 transition-all duration-300">
          ← Back to Presentations
        </button>

        <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-8 leading-tight">{presentation.title}</h1>

        <div className="flex items-center mb-8 border-b pb-6 border-gray-200">
          <img src={getGoogleDriveDirectImageUrl(generatePresenterImage(presenter.name, presenter.image))} alt={presenter.name} className="w-20 h-20 rounded-full object-cover mr-4 border-4 border-purple-400 shadow-md" />
          <div>
            <p className="text-xl font-semibold text-gray-800">{presenter.name}</p>
            <p className="text-md text-gray-600">{presenter.bio}</p>
          </div>
        </div>

        {spotlightStory ? (
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed whitespace-pre-line">
            <p>{spotlightStory}</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
            <h3 className="text-xl font-bold mb-2">Spotlight story not available yet</h3>
            <p>This presentation has slides, but no spotlight story has been added.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const TestimonialsView = ({ testimonials }) => (
  <div className="p-6 md:p-10 min-h-[calc(100vh-80px)]">
    <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 pb-8 text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-pink-500">
      What Our Community Says
    </h2>
    {Array.isArray(testimonials) && testimonials.length > 0 ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {testimonials.map((t) => (
          <div key={t.id} className="bg-white rounded-2xl shadow-xl p-6 border border-gray-200 flex flex-col bg-gradient-to-br from-white to-green-50">
            <p className="text-gray-700 italic mb-4">{t.quote}</p>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-center text-gray-600 text-lg">No testimonials available. Add some from the Admin Panel!</p>
    )}
  </div>
);

const ResourcesView = ({ resources }) => (
  <div className="p-6 md:p-10 min-h-[calc(100vh-80px)]">
    <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-blue-500">
      Additional Resources
    </h2>
    {Array.isArray(resources) && resources.length > 0 ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {resources.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl shadow-xl overflow-hidden p-6 border border-gray-200 bg-gradient-to-br from-white to-blue-50 flex flex-col items-center text-center">
            {r.thumbnail && <img src={r.thumbnail} alt={r.name} className="w-full h-32 object-cover rounded-lg mb-4 shadow-sm" />}
            <h4 className="text-xl font-bold text-gray-900 mb-2">{r.name}</h4>
            <p className="text-sm text-gray-600 mb-4 flex-grow">{r.description}</p>
            <a href={r.link} target="_blank" rel="noopener noreferrer" className="px-5 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold rounded-full shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 text-sm mt-auto">
              View Resource
            </a>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-center text-gray-600 text-lg">No resources available. Add some from the Admin Panel!</p>
    )}
  </div>
);

const Home = ({ onTalkToPrisma, isLoadingData }) => (
  <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] p-4 text-gray-800">
    <h1 className="text-6xl md:text-8xl font-extrabold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 animate-pulse-slow">
      Prisma Archives
    </h1>
    <div className="flex flex-col md:flex-row items-center justify-center max-w-5xl mx-auto bg-white bg-opacity-90 rounded-3xl shadow-2xl p-8 md:p-12 transition-transform duration-300 ease-in-out">
      <div className="flex-shrink-0 mb-8 md:mb-0 md:mr-12">
        <img src="/prisma-avatar.png" alt="Prisma AI Avatar" className="w-60 h-60 rounded-full object-cover mb-4 border-4 border-purple-400 shadow-lg" />
      </div>
      <div className="text-center md:text-left">
        <p className="text-xl md:text-2xl leading-relaxed mb-8 text-gray-700">
          Welcome to Prisma Archives, the repository of all Idea Sharing Session presentations. Here, you can dive into the insightful and thought-provoking sessions conducted by our diverse presenters. Get to know their stories, experiences, and innovative ideas that are shaping the future.
        </p>
        <button onClick={onTalkToPrisma} className="px-8 py-4 bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-bold text-xl rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-orange-300">
          ✨ Talk to Prisma AI
        </button>
      </div>
    </div>
    {isLoadingData && (
      <div className="w-full max-w-xs mt-4 bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
        <div className="bg-blue-600 h-2.5 rounded-full animate-pulse" style={{ width: '100%' }} />
      </div>
    )}
  </div>
);

const BulletinsView = ({ bulletins }) => {
  const sortedBulletins = React.useMemo(() => {
    if (!Array.isArray(bulletins)) return [];
    return [...bulletins].sort((a, b) => new Date(b.when) - new Date(a.when));
  }, [bulletins]);

  const [currentBulletinIndex, setCurrentBulletinIndex] = useState(0);

  useEffect(() => {
    setCurrentBulletinIndex(0);
  }, [bulletins]);

  const showNextBulletin = () => {
    if (sortedBulletins.length > 0) {
      setCurrentBulletinIndex((prevIndex) => (prevIndex + 1) % sortedBulletins.length);
    }
  };

  const showPrevBulletin = () => {
    if (sortedBulletins.length > 0) {
      setCurrentBulletinIndex((prevIndex) => (prevIndex - 1 + sortedBulletins.length) % sortedBulletins.length);
    }
  };

  const currentBulletin = sortedBulletins.length > 0 ? sortedBulletins[currentBulletinIndex] : null;

  return (
    <div className="p-6 md:p-10 min-h-[calc(100vh-80px)]">
      <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 pb-8 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
        Bulletins
      </h2>
      <section className="mt-8 w-full max-w-6xl bg-white rounded-lg shadow-xl mx-auto p-0 overflow-hidden">
        {currentBulletin ? (
          <div>
            <div className="flex flex-col md:flex-row">
              <div className="w-full md:w-1/4 p-6 flex flex-col">
                <h4 className="text-2xl font-bold text-gray-900 mb-4">{currentBulletin.what}</h4>
                <p className="text-base text-gray-700 mb-2"><span className="font-semibold">When:</span> {currentBulletin.when}</p>
                <p className="text-base text-gray-700 mb-2"><span className="font-semibold">Where:</span> {currentBulletin.where}</p>
                <p className="text-base text-gray-700 mb-4"><span className="font-semibold">Who:</span> {currentBulletin.who}</p>
              </div>
              {currentBulletin.image && (
                <div className="w-full md:w-3/4 bg-gray-100 h-[60vh] overflow-y-auto">
                  <a href={getGoogleDriveDirectImageUrl(currentBulletin.image)} target="_blank" rel="noopener noreferrer" className="w-full h-full block">
                    <img src={getGoogleDriveThumbnailUrl(currentBulletin.image)} alt={currentBulletin.what} className="w-full h-auto" />
                  </a>
                </div>
              )}
            </div>
            {sortedBulletins.length > 1 && (
              <div className="flex justify-center p-4 border-t border-gray-200">
                <button onClick={showPrevBulletin} className="px-6 py-2 bg-blue-500 text-white font-semibold rounded-full shadow-md hover:bg-blue-600 transition-all duration-300 text-sm mx-2">← Previous</button>
                <button onClick={showNextBulletin} className="px-6 py-2 bg-blue-500 text-white font-semibold rounded-full shadow-md hover:bg-blue-600 transition-all duration-300 text-sm mx-2">Next →</button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-gray-600 text-lg p-6">No bulletins available.</p>
        )}
      </section>
    </div>
  );
};

const PresenterPresentationCard = ({ card, onViewSpotlightStory }) => {
  const [activePresentationIndex, setActivePresentationIndex] = useState(0);
  const totalPresentations = card.presentations.length;
  const safeActiveIndex = totalPresentations > 0 ? Math.min(activePresentationIndex, totalPresentations - 1) : 0;
  const activePresentation = totalPresentations > 0 ? card.presentations[safeActiveIndex] : null;
  const cardSummary = getPresentationCardSummary(activePresentation);
  const spotlightAvailable = hasSpotlightStory(activePresentation);
  const canNavigate = totalPresentations > 1;

  useEffect(() => {
    setActivePresentationIndex(0);
  }, [card.presenter.id, card.presenter.name, totalPresentations]);

  const goToPreviousPresentation = () => {
    if (!canNavigate) return;
    setActivePresentationIndex((currentIndex) => (currentIndex - 1 + totalPresentations) % totalPresentations);
  };

  const goToNextPresentation = () => {
    if (!canNavigate) return;
    setActivePresentationIndex((currentIndex) => (currentIndex + 1) % totalPresentations);
  };

  return (
    <div className="relative bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 p-8 flex flex-col min-h-full">
      <div className="absolute top-5 right-5 rounded-full bg-indigo-50 text-indigo-700 px-4 py-2 text-sm font-bold shadow-sm">
        ✨ {totalPresentations} Presentation{totalPresentations === 1 ? '' : 's'}
      </div>

      <div className="flex flex-col items-center text-center pt-4">
        <img
          src={getGoogleDriveThumbnailUrl(card.presenter.image)}
          alt={card.presenter.name}
          className="w-32 h-32 rounded-full object-cover border-4 border-blue-300 shadow-md mb-5"
        />
        <h4 className="text-2xl font-extrabold text-gray-900">{card.presenter.name}</h4>
        {card.presenter.bio && (
          <p className="text-sm font-semibold uppercase tracking-wide text-indigo-600 mt-2">{card.presenter.bio}</p>
        )}
      </div>

      <div className="border-t border-gray-100 mt-8 pt-7 flex-grow flex flex-col">
        {activePresentation ? (
          <>
            <div className="flex items-center justify-between gap-4 mb-5">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]">
                Presentation {safeActiveIndex + 1} of {totalPresentations}
              </p>

              {canNavigate && (
                <div className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-2">
                  <button
                    type="button"
                    onClick={goToPreviousPresentation}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-gray-700 hover:bg-white hover:shadow transition-all"
                    aria-label="Previous presentation"
                  >
                    ←
                  </button>
                  <div className="flex items-center gap-1">
                    {card.presentations.map((presentation, index) => (
                      <button
                        type="button"
                        key={presentation.id || `${card.presenter.id}-dot-${index}`}
                        onClick={() => setActivePresentationIndex(index)}
                        className={`h-2 rounded-full transition-all ${index === safeActiveIndex ? 'w-5 bg-indigo-600' : 'w-2 bg-gray-300 hover:bg-gray-400'}`}
                        aria-label={`Go to presentation ${index + 1}`}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={goToNextPresentation}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-gray-700 hover:bg-white hover:shadow transition-all"
                    aria-label="Next presentation"
                  >
                    →
                  </button>
                </div>
              )}
            </div>

            <h5 className="text-2xl font-extrabold text-gray-900 leading-snug mb-4">{activePresentation.title}</h5>
            {cardSummary ? (
              <p
                className="text-gray-600 leading-relaxed mb-7"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {cardSummary}
              </p>
            ) : (
              <p className="text-gray-500 leading-relaxed mb-7">
                View the slides or open the spotlight story to see the full presentation details.
              </p>
            )}

            <div className="space-y-3 mt-auto">
              {activePresentation.slidesLink ? (
                <a
                  href={activePresentation.slidesLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold rounded-2xl shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 text-sm"
                >
                  ▶ View Slides
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  className="w-full px-5 py-3 bg-gray-200 text-gray-500 font-bold rounded-2xl cursor-not-allowed text-sm"
                >
                  Slides Not Available
                </button>
              )}

              {spotlightAvailable ? (
                <button
                  type="button"
                  onClick={() => onViewSpotlightStory(activePresentation)}
                  className="w-full px-5 py-3 bg-gradient-to-r from-green-400 to-teal-500 text-white font-bold rounded-2xl shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 text-sm"
                >
                  ✨ Spotlight Story
                </button>
              ) : (
                <button
                  type="button"
                  disabled
                  title="No spotlight story has been added for this presentation."
                  className="w-full px-5 py-3 bg-gray-100 text-gray-400 font-bold rounded-2xl border border-gray-200 cursor-not-allowed text-sm"
                >
                  Spotlight Story Not Available
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="flex-grow flex items-center justify-center text-center text-gray-500 italic">
            No presentations assigned to this presenter yet.
          </div>
        )}
      </div>
    </div>
  );
};

const PresentationsView = ({ presenters, presentations, onViewSpotlightStory }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');

  const presenterCards = React.useMemo(() => {
    const cardsMap = new Map();

    (presenters || []).forEach((presenter) => {
      cardsMap.set(presenter.id, {
        presenter: {
          ...presenter,
          image: generatePresenterImage(presenter.name, presenter.image),
        },
        presentations: [],
      });
    });

    (presentations || []).forEach((presentation) => {
      const resolvedPresenter = resolvePresenterForPresentation(presentation, presenters || []);
      const key = presentation.presenterId || resolvedPresenter.id || `legacy-${normalizeName(resolvedPresenter.name)}` || presentation.id;

      if (!cardsMap.has(key)) {
        cardsMap.set(key, {
          presenter: resolvedPresenter,
          presentations: [],
        });
      }

      const card = cardsMap.get(key);
      card.presentations.push({
        ...presentation,
        presenter: card.presenter,
      });
    });

    return Array.from(cardsMap.values()).sort((a, b) => a.presenter.name.localeCompare(b.presenter.name));
  }, [presenters, presentations]);

  const filteredPresenterCards = React.useMemo(() => {
    if (!searchTerm.trim()) return presenterCards;
    const term = searchTerm.toLowerCase();
    return presenterCards.filter((card) =>
      card.presenter.name.toLowerCase().includes(term) ||
      card.presentations.some((presentation) => presentation.title.toLowerCase().includes(term))
    );
  }, [searchTerm, presenterCards]);

  const handlePresenterClick = (presenterName) => {
    setSearchTerm(presenterName);
    setViewMode('list');
  };

  return (
    <div className="p-6 md:p-10 min-h-[calc(100vh-80px)]">
      <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 pb-8 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-teal-400">
        Idea Sharing Sessions
      </h2>

      <div className="flex flex-col md:flex-row justify-center items-center gap-4 mb-8">
        <h3 className="text-3xl font-semibold text-gray-800 whitespace-nowrap">Our Presenters</h3>
        <div className="w-full max-w-md">
          <input
            type="text"
            placeholder="Search by presenter or presentation title..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center">
          <span className={`mr-2 cursor-pointer ${viewMode === 'list' ? 'font-bold' : ''}`} onClick={() => setViewMode('list')}>List</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={viewMode === 'grid'} onChange={() => setViewMode((prev) => (prev === 'list' ? 'grid' : 'list'))} className="sr-only peer" />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600" />
          </label>
          <span className={`ml-2 cursor-pointer ${viewMode === 'grid' ? 'font-bold' : ''}`} onClick={() => setViewMode('grid')}>Grid</span>
        </div>
      </div>

      <section className="mb-16">
        {viewMode === 'list' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 items-stretch">
            {filteredPresenterCards.length > 0 ? (
              filteredPresenterCards.map((card) => (
                <PresenterPresentationCard
                  key={card.presenter.id || card.presenter.name}
                  card={card}
                  onViewSpotlightStory={onViewSpotlightStory}
                />
              ))
            ) : (
              <p className="text-center text-gray-600 col-span-full">No presenters or presentations found matching your search.</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredPresenterCards.map((card) => (
              <div key={card.presenter.id || card.presenter.name} className="relative aspect-square overflow-hidden cursor-pointer group rounded-2xl shadow-lg" onClick={() => handlePresenterClick(card.presenter.name)}>
                <img src={getGoogleDriveThumbnailUrl(card.presenter.image)} alt={card.presenter.name} className="w-full h-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-110" />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-4">
                  <span className="text-white text-lg font-bold text-center">{card.presenter.name}</span>
                  <span className="text-white text-sm text-center mt-1">{card.presentations.length} presentation{card.presentations.length === 1 ? '' : 's'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

const PaginationControls = ({ totalPages, currentPage, onPageChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex justify-center mt-4 flex-wrap gap-2">
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
        <button key={number} onClick={() => onPageChange(number)} className={`px-4 py-2 rounded-md ${currentPage === number ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
          {number}
        </button>
      ))}
    </div>
  );
};

const AdminPanel = ({
  presenters,
  presentations,
  testimonials,
  resources,
  bulletins,
  addPresenter,
  updatePresenter,
  deletePresenter,
  addPresentation,
  updatePresentation,
  deletePresentation,
  addTestimonial,
  updateTestimonial,
  deleteTestimonial,
  addResource,
  updateResource,
  deleteResource,
  addBulletin,
  updateBulletin,
  deleteBulletin,
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingItemType, setEditingItemType] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToDeleteType, setItemToDeleteType] = useState(null);
  const [submissionMessage, setSubmissionMessage] = useState(null);
  const [adminActiveTab, setAdminActiveTab] = useState('presenters');
  const [presenterSearchTerm, setPresenterSearchTerm] = useState('');
  const [presentationSearchTerm, setPresentationSearchTerm] = useState('');
  const [currentPages, setCurrentPages] = useState({
    presenters: 1,
    presentations: 1,
    testimonials: 1,
    resources: 1,
    bulletins: 1,
  });
  const itemsPerPage = 10;

  useEffect(() => {
    if (!submissionMessage) return undefined;
    const timer = setTimeout(() => setSubmissionMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [submissionMessage]);

  useEffect(() => {
    setCurrentPages((prev) => ({ ...prev, presenters: 1 }));
  }, [presenterSearchTerm]);

  useEffect(() => {
    setCurrentPages((prev) => ({ ...prev, presentations: 1 }));
  }, [presentationSearchTerm]);

  const buttonClasses = 'px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white font-bold rounded-lg shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-green-300';

  const getPresenterName = (presentation) => {
    const presenter = resolvePresenterForPresentation(presentation, presenters);
    return presenter.name;
  };

  const handleSaveItem = async (formData) => {
    try {
      if (editingItemType === 'presenters') {
        const payload = {
          name: formData.name || '',
          bio: formData.bio || '',
          image: generatePresenterImage(formData.name, formData.image),
        };
        if (editingItem) {
          await updatePresenter(formData.id, payload);
          setSubmissionMessage({ text: 'Presenter updated successfully!', type: 'success' });
        } else {
          await addPresenter(payload);
          setSubmissionMessage({ text: 'Presenter added successfully!', type: 'success' });
        }
      } else if (editingItemType === 'presentations') {
        const payload = {
          title: formData.title || '',
          presenterId: formData.presenterId || '',
          slidesLink: formData.slidesLink || '',
          description: formData.description || '',
          spotlightStory: formData.spotlightStory || '',
        };
        if (editingItem) {
          await updatePresentation(formData.id, payload);
          setSubmissionMessage({ text: 'Presentation updated successfully!', type: 'success' });
        } else {
          await addPresentation(payload);
          setSubmissionMessage({ text: 'Presentation added successfully!', type: 'success' });
        }
      } else if (editingItemType === 'testimonials') {
        if (editingItem) {
          await updateTestimonial(formData.id, formData);
          setSubmissionMessage({ text: 'Testimonial updated successfully!', type: 'success' });
        } else {
          await addTestimonial(formData);
          setSubmissionMessage({ text: 'Testimonial added successfully!', type: 'success' });
        }
      } else if (editingItemType === 'resources') {
        const payload = { ...formData, thumbnail: generateDefaultThumbnail(formData.link, formData.thumbnail) };
        if (editingItem) {
          await updateResource(payload.id, payload);
          setSubmissionMessage({ text: 'Resource updated successfully!', type: 'success' });
        } else {
          await addResource(payload);
          setSubmissionMessage({ text: 'Resource added successfully!', type: 'success' });
        }
      } else if (editingItemType === 'bulletins') {
        if (editingItem) {
          await updateBulletin(formData.id, formData);
          setSubmissionMessage({ text: 'Bulletin updated successfully!', type: 'success' });
        } else {
          await addBulletin(formData);
          setSubmissionMessage({ text: 'Bulletin added successfully!', type: 'success' });
        }
      }
    } catch (error) {
      setSubmissionMessage({ text: `Failed to ${editingItem ? 'update' : 'add'} ${editingItemType?.slice(0, -1)}: ${error.message || 'Unknown error'}`, type: 'error' });
    } finally {
      setShowEditModal(false);
      setEditingItem(null);
      setEditingItemType(null);
    }
  };

  const onEditClick = (item, type) => {
    setEditingItem(item);
    setEditingItemType(type);
    setShowEditModal(true);
  };

  const onDeleteClick = (item, type) => {
    setItemToDelete(item);
    setItemToDeleteType(type);
    setShowConfirmModal(true);
  };

  const onConfirmDelete = async () => {
    try {
      if (itemToDeleteType === 'presenters') await deletePresenter(itemToDelete.id);
      else if (itemToDeleteType === 'presentations') await deletePresentation(itemToDelete.id);
      else if (itemToDeleteType === 'testimonials') await deleteTestimonial(itemToDelete.id);
      else if (itemToDeleteType === 'resources') await deleteResource(itemToDelete.id);
      else if (itemToDeleteType === 'bulletins') await deleteBulletin(itemToDelete.id);

      setSubmissionMessage({ text: `${itemToDeleteType?.slice(0, -1)} deleted successfully!`, type: 'success' });
    } catch (error) {
      setSubmissionMessage({ text: `Failed to delete ${itemToDeleteType?.slice(0, -1)}: ${error.message || 'Unknown error'}`, type: 'error' });
    } finally {
      setShowConfirmModal(false);
      setItemToDelete(null);
      setItemToDeleteType(null);
    }
  };

  const onCancelDelete = () => {
    setShowConfirmModal(false);
    setItemToDelete(null);
    setItemToDeleteType(null);
  };

  const paginate = (type, pageNumber) => setCurrentPages((prev) => ({ ...prev, [type]: pageNumber }));

  const sortedPresenters = React.useMemo(() => [...(presenters || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')), [presenters]);
  const filteredPresenters = React.useMemo(() => {
    const query = presenterSearchTerm.trim().toLowerCase();
    if (!query) return sortedPresenters;

    return sortedPresenters.filter((presenter) => {
      const searchableText = `${presenter.name || ''} ${presenter.bio || ''}`.toLowerCase();
      return searchableText.includes(query);
    });
  }, [sortedPresenters, presenterSearchTerm]);

  const sortedPresentations = React.useMemo(
    () => [...(presentations || [])].sort((a, b) => (a.title || '').localeCompare(b.title || '')),
    [presentations]
  );

  const filteredPresentations = React.useMemo(() => {
    const query = presentationSearchTerm.trim().toLowerCase();
    if (!query) return sortedPresentations;

    return sortedPresentations.filter((presentation) => {
      const presenter = resolvePresenterForPresentation(presentation, presenters);
      const searchableText = [
        presentation.title,
        presenter.name,
        presentation.slidesLink,
        presentation.description,
        presentation.spotlightStory,
        presentation.summary,
        presentation.shortDescription,
      ].filter(Boolean).join(' ').toLowerCase();

      return searchableText.includes(query);
    });
  }, [sortedPresentations, presenters, presentationSearchTerm]);

  const sortedBulletins = React.useMemo(() => [...(bulletins || [])].sort((a, b) => new Date(b.when) - new Date(a.when)), [bulletins]);

  const paginatedData = React.useMemo(() => {
    const data = {
      presenters: filteredPresenters,
      presentations: filteredPresentations,
      testimonials: testimonials || [],
      resources: resources || [],
      bulletins: sortedBulletins,
    };

    return Object.keys(data).reduce((acc, key) => {
      const currentPage = currentPages[key];
      const indexOfLastItem = currentPage * itemsPerPage;
      const indexOfFirstItem = indexOfLastItem - itemsPerPage;
      acc[key] = data[key].slice(indexOfFirstItem, indexOfLastItem);
      return acc;
    }, {});
  }, [filteredPresenters, filteredPresentations, testimonials, resources, sortedBulletins, currentPages]);

  const totalPages = {
    presenters: Math.ceil(filteredPresenters.length / itemsPerPage),
    presentations: Math.ceil(filteredPresentations.length / itemsPerPage),
    testimonials: Math.ceil((testimonials || []).length / itemsPerPage),
    resources: Math.ceil((resources || []).length / itemsPerPage),
    bulletins: Math.ceil(sortedBulletins.length / itemsPerPage),
  };

  const tabButton = (key, label) => (
    <button
      onClick={() => setAdminActiveTab(key)}
      className={`px-6 py-3 text-lg font-semibold rounded-t-lg transition-all duration-300 ${adminActiveTab === key ? 'bg-white text-purple-700 shadow-md border-t border-l border-r border-gray-200 -mb-px' : 'text-gray-600 hover:bg-gray-100'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="p-6 md:p-10 min-h-[calc(100vh-80px)]">
      <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-500">
        Admin
      </h2>

      {submissionMessage && (
        <div className={`p-4 mb-6 rounded-lg text-white font-semibold text-center ${submissionMessage.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          {submissionMessage.text}
        </div>
      )}

      <div className="flex flex-wrap justify-center mb-8 border-b border-gray-200">
        {tabButton('presenters', 'Presenters')}
        {tabButton('presentations', 'Presentations')}
        {tabButton('testimonials', 'Testimonials')}
        {tabButton('resources', 'Resources')}
        {tabButton('bulletins', 'Bulletins')}
      </div>

      <div className="bg-white bg-opacity-90 rounded-b-2xl rounded-tr-2xl shadow-xl p-8 border border-gray-200">
        {adminActiveTab === 'presenters' && (
          <>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6 border-b pb-4 border-gray-200">Manage Presenters</h3>
            <div className="flex flex-col md:flex-row gap-3 mb-6">
              <button onClick={() => { setEditingItem(null); setEditingItemType('presenters'); setShowEditModal(true); }} className={buttonClasses}>
                Add New Presenter
              </button>
            </div>

            <div className="mb-6">
              <label htmlFor="presenter-admin-search" className="block text-sm font-semibold text-gray-700 mb-2">
                Search presenters
              </label>
              <div className="flex flex-col md:flex-row gap-3 md:items-center">
                <input
                  id="presenter-admin-search"
                  type="text"
                  placeholder="Search by presenter name or bio..."
                  value={presenterSearchTerm}
                  onChange={(event) => setPresenterSearchTerm(event.target.value)}
                  className="w-full md:max-w-md p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all duration-200"
                />
                {presenterSearchTerm && (
                  <button
                    type="button"
                    onClick={() => setPresenterSearchTerm('')}
                    className="px-4 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors duration-200"
                  >
                    Clear
                  </button>
                )}
                <p className="text-sm text-gray-500">
                  Showing {filteredPresenters.length} of {sortedPresenters.length} presenters
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-md">
                <thead>
                  <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                    <th className="py-3 px-6 text-left">Name</th>
                    <th className="py-3 px-6 text-left">Bio</th>
                    <th className="py-3 px-6 text-center">Presentations</th>
                    <th className="py-3 px-6 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 text-sm font-light">
                  {paginatedData.presenters.map((presenter) => {
                    const presentationCount = (presentations || []).filter((p) => p.presenterId === presenter.id).length;
                    return (
                      <tr key={presenter.id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-6 text-left whitespace-nowrap">{presenter.name}</td>
                        <td className="py-3 px-6 text-left">{presenter.bio}</td>
                        <td className="py-3 px-6 text-center">{presentationCount}</td>
                        <td className="py-3 px-6 text-center">
                          <div className="flex item-center justify-center space-x-2">
                            <button onClick={() => onEditClick(presenter, 'presenters')} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded-full text-xs">Edit</button>
                            <button onClick={() => onDeleteClick(presenter, 'presenters')} className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded-full text-xs">Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPresenters.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-3 px-6 text-center text-gray-500">
                        {sortedPresenters.length === 0 ? 'No presenters available.' : 'No presenters found matching your search.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <PaginationControls totalPages={totalPages.presenters} currentPage={currentPages.presenters} onPageChange={(page) => paginate('presenters', page)} />
          </>
        )}

        {adminActiveTab === 'presentations' && (
          <>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6 border-b pb-4 border-gray-200">Manage Presentations</h3>
            <button onClick={() => { setEditingItem(null); setEditingItemType('presentations'); setShowEditModal(true); }} className={`${buttonClasses} mb-6`}>
              Add New Presentation
            </button>
            {sortedPresenters.length === 0 && (
              <div className="p-4 mb-6 rounded-lg bg-yellow-100 text-yellow-800 font-semibold">
                Create at least one presenter first before adding a presentation.
              </div>
            )}

            <div className="mb-6">
              <label htmlFor="presentation-admin-search" className="block text-sm font-semibold text-gray-700 mb-2">
                Search presentations
              </label>
              <div className="flex flex-col md:flex-row gap-3 md:items-center">
                <input
                  id="presentation-admin-search"
                  type="text"
                  placeholder="Search by title, presenter, link, overview, or spotlight story..."
                  value={presentationSearchTerm}
                  onChange={(event) => setPresentationSearchTerm(event.target.value)}
                  className="w-full md:max-w-md p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all duration-200"
                />
                {presentationSearchTerm && (
                  <button
                    type="button"
                    onClick={() => setPresentationSearchTerm('')}
                    className="px-4 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors duration-200"
                  >
                    Clear
                  </button>
                )}
                <p className="text-sm text-gray-500">
                  Showing {filteredPresentations.length} of {sortedPresentations.length} presentations
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-md">
                <thead>
                  <tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal">
                    <th className="py-3 px-6 text-left">Title</th>
                    <th className="py-3 px-6 text-left">Presenter</th>
                    <th className="py-3 px-6 text-left">Slides Link</th>
                    <th className="py-3 px-6 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-gray-600 text-sm font-light">
                  {paginatedData.presentations.map((presentation) => (
                    <tr key={presentation.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-6 text-left whitespace-nowrap">{presentation.title}</td>
                      <td className="py-3 px-6 text-left">{getPresenterName(presentation)}</td>
                      <td className="py-3 px-6 text-left truncate max-w-xs"><a href={presentation.slidesLink} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{presentation.slidesLink}</a></td>
                      <td className="py-3 px-6 text-center">
                        <div className="flex item-center justify-center space-x-2">
                          <button onClick={() => onEditClick(presentation, 'presentations')} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded-full text-xs">Edit</button>
                          <button onClick={() => onDeleteClick(presentation, 'presentations')} className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded-full text-xs">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredPresentations.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-3 px-6 text-center text-gray-500">
                        {sortedPresentations.length === 0 ? 'No presentations available.' : 'No presentations found matching your search.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <PaginationControls totalPages={totalPages.presentations} currentPage={currentPages.presentations} onPageChange={(page) => paginate('presentations', page)} />
          </>
        )}

        {adminActiveTab === 'testimonials' && (
          <>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6 border-b pb-4 border-gray-200">Manage Testimonials</h3>
            <button onClick={() => { setEditingItem(null); setEditingItemType('testimonials'); setShowEditModal(true); }} className={`${buttonClasses} mb-6`}>Add New Testimonial</button>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-md">
                <thead><tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal"><th className="py-3 px-6 text-left">Quote</th><th className="py-3 px-6 text-center">Actions</th></tr></thead>
                <tbody className="text-gray-600 text-sm font-light">
                  {paginatedData.testimonials.map((testimonial) => (
                    <tr key={testimonial.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-6 text-left italic">&quot;{testimonial.quote}&quot;</td>
                      <td className="py-3 px-6 text-center"><div className="flex item-center justify-center space-x-2"><button onClick={() => onEditClick(testimonial, 'testimonials')} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded-full text-xs">Edit</button><button onClick={() => onDeleteClick(testimonial, 'testimonials')} className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded-full text-xs">Delete</button></div></td>
                    </tr>
                  ))}
                  {(testimonials || []).length === 0 && <tr><td colSpan={2} className="py-3 px-6 text-center text-gray-500">No testimonials available.</td></tr>}
                </tbody>
              </table>
            </div>
            <PaginationControls totalPages={totalPages.testimonials} currentPage={currentPages.testimonials} onPageChange={(page) => paginate('testimonials', page)} />
          </>
        )}

        {adminActiveTab === 'resources' && (
          <>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6 border-b pb-4 border-gray-200">Manage Resources</h3>
            <button onClick={() => { setEditingItem(null); setEditingItemType('resources'); setShowEditModal(true); }} className={`${buttonClasses} mb-6`}>Add New Resource</button>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-md">
                <thead><tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal"><th className="py-3 px-6 text-left">Name</th><th className="py-3 px-6 text-left">Link</th><th className="py-3 px-6 text-left">Description</th><th className="py-3 px-6 text-center">Actions</th></tr></thead>
                <tbody className="text-gray-600 text-sm font-light">
                  {paginatedData.resources.map((resource) => (
                    <tr key={resource.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-6 text-left whitespace-nowrap">{resource.name}</td>
                      <td className="py-3 px-6 text-left truncate max-w-xs"><a href={resource.link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{resource.link}</a></td>
                      <td className="py-3 px-6 text-left">{resource.description}</td>
                      <td className="py-3 px-6 text-center"><div className="flex item-center justify-center space-x-2"><button onClick={() => onEditClick(resource, 'resources')} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded-full text-xs">Edit</button><button onClick={() => onDeleteClick(resource, 'resources')} className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded-full text-xs">Delete</button></div></td>
                    </tr>
                  ))}
                  {(resources || []).length === 0 && <tr><td colSpan={4} className="py-3 px-6 text-center text-gray-500">No resources available.</td></tr>}
                </tbody>
              </table>
            </div>
            <PaginationControls totalPages={totalPages.resources} currentPage={currentPages.resources} onPageChange={(page) => paginate('resources', page)} />
          </>
        )}

        {adminActiveTab === 'bulletins' && (
          <>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6 border-b pb-4 border-gray-200">Manage Bulletins</h3>
            <button onClick={() => { setEditingItem(null); setEditingItemType('bulletins'); setShowEditModal(true); }} className={`${buttonClasses} mb-6`}>Add New Bulletin</button>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-md">
                <thead><tr className="bg-gray-100 text-gray-600 uppercase text-sm leading-normal"><th className="py-3 px-6 text-left">What</th><th className="py-3 px-6 text-left">When</th><th className="py-3 px-6 text-left">Where</th><th className="py-3 px-6 text-left">Who</th><th className="py-3 px-6 text-left">Display Until</th><th className="py-3 px-6 text-center">Actions</th></tr></thead>
                <tbody className="text-gray-600 text-sm font-light">
                  {paginatedData.bulletins.map((bulletin) => (
                    <tr key={bulletin.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-3 px-6 text-left whitespace-nowrap">{bulletin.what}</td><td className="py-3 px-6 text-left">{bulletin.when}</td><td className="py-3 px-6 text-left">{bulletin.where}</td><td className="py-3 px-6 text-left">{bulletin.who}</td><td className="py-3 px-6 text-left">{bulletin.displayUntil}</td>
                      <td className="py-3 px-6 text-center"><div className="flex item-center justify-center space-x-2"><button onClick={() => onEditClick(bulletin, 'bulletins')} className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded-full text-xs">Edit</button><button onClick={() => onDeleteClick(bulletin, 'bulletins')} className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded-full text-xs">Delete</button></div></td>
                    </tr>
                  ))}
                  {(bulletins || []).length === 0 && <tr><td colSpan={6} className="py-3 px-6 text-center text-gray-500">No bulletins available.</td></tr>}
                </tbody>
              </table>
            </div>
            <PaginationControls totalPages={totalPages.bulletins} currentPage={currentPages.bulletins} onPageChange={(page) => paginate('bulletins', page)} />
          </>
        )}
      </div>

      {showEditModal && editingItemType && (
        <ItemFormModal item={editingItem} itemType={editingItemType} presenters={sortedPresenters} onSave={handleSaveItem} onClose={() => setShowEditModal(false)} />
      )}

      {showConfirmModal && itemToDelete && itemToDeleteType && (
        <ConfirmationModal message={`Are you sure you want to delete this ${itemToDeleteType.slice(0, -1)}? This action cannot be undone.`} onConfirm={onConfirmDelete} onCancel={onCancelDelete} />
      )}

      <footer className="mt-8 pt-4 border-t border-gray-200 text-center text-sm text-gray-500">
        <p>Prisma Archives Admin Panel - Version 1.1.4</p>
      </footer>
    </div>
  );
};

const ItemFormModal = ({ item, itemType, presenters = [], onSave, onClose }) => {
  const getInitialFormData = () => {
    if (!item) return {};

    if (itemType === 'presentations' && !item.presenterId && item.presenter?.name) {
      const matchedPresenter = presenters.find((presenter) => normalizeName(presenter.name) === normalizeName(item.presenter.name));
      return { ...item, presenterId: matchedPresenter?.id || '' };
    }

    return item;
  };

  const [formData, setFormData] = useState(getInitialFormData);
  const [presenterSelectSearchTerm, setPresenterSelectSearchTerm] = useState('');
  const isNew = !item;

  useEffect(() => {
    setFormData(getInitialFormData());
  }, [item, itemType]);

  useEffect(() => {
    setPresenterSelectSearchTerm('');
  }, [item, itemType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const inputClasses = 'w-full p-3 mb-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200';
  const buttonClasses = 'px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white font-bold rounded-lg shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-green-300 disabled:opacity-60 disabled:transform-none';
  const presentationSubmitDisabled = itemType === 'presentations' && presenters.length === 0;
  const filteredModalPresenters = React.useMemo(() => {
    const query = presenterSelectSearchTerm.trim().toLowerCase();
    if (!query) return presenters;

    return presenters.filter((presenter) => {
      const searchableText = `${presenter.name || ''} ${presenter.bio || ''}`.toLowerCase();
      return searchableText.includes(query);
    });
  }, [presenters, presenterSelectSearchTerm]);
  const selectedPresenter = React.useMemo(
    () => presenters.find((presenter) => presenter.id === formData.presenterId),
    [presenters, formData.presenterId]
  );
  const displayedModalPresenters = React.useMemo(() => {
    if (!selectedPresenter) return filteredModalPresenters;
    const selectedAlreadyVisible = filteredModalPresenters.some((presenter) => presenter.id === selectedPresenter.id);
    return selectedAlreadyVisible ? filteredModalPresenters : [selectedPresenter, ...filteredModalPresenters];
  }, [filteredModalPresenters, selectedPresenter]);

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden">
        <div className="p-4 bg-gradient-to-r from-blue-600 to-teal-500 text-white flex justify-between items-center rounded-t-2xl">
          <h3 className="text-xl font-bold">{isNew ? `Add New ${itemType.slice(0, -1)}` : `Edit ${itemType.slice(0, -1)}`}</h3>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl font-bold">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[70vh]">
          {itemType === 'presenters' && (
            <>
              <input type="text" name="name" placeholder="Presenter Name" value={formData.name || ''} onChange={handleChange} className={inputClasses} required />
              <input type="text" name="bio" placeholder="Presenter Bio (Optional)" value={formData.bio || ''} onChange={handleChange} className={inputClasses} />
              <input type="url" name="image" placeholder="Presenter Image URL (Optional)" value={formData.image || ''} onChange={handleChange} className={inputClasses} />
            </>
          )}

          {itemType === 'presentations' && (
            <>
              <input
                type="text"
                placeholder="Search presenter before selecting..."
                value={presenterSelectSearchTerm}
                onChange={(event) => setPresenterSelectSearchTerm(event.target.value)}
                className={inputClasses}
                disabled={presenters.length === 0}
              />
              <select name="presenterId" value={formData.presenterId || ''} onChange={handleChange} className={inputClasses} required disabled={presenters.length === 0}>
                <option value="">{presenters.length === 0 ? 'Create a presenter first' : 'Select Presenter'}</option>
                {displayedModalPresenters.map((presenter) => <option key={presenter.id} value={presenter.id}>{presenter.name}</option>)}
                {presenters.length > 0 && displayedModalPresenters.length === 0 && <option value="" disabled>No presenters found</option>}
              </select>
              {presenterSelectSearchTerm && (
                <p className="-mt-2 mb-4 text-sm text-gray-500">
                  Showing {filteredModalPresenters.length} of {presenters.length} presenters
                </p>
              )}
              <input type="text" name="title" placeholder="Presentation Title" value={formData.title || ''} onChange={handleChange} className={inputClasses} required />
              <input type="url" name="slidesLink" placeholder="Slides Link (URL)" value={formData.slidesLink || ''} onChange={handleChange} className={inputClasses} required />
              <textarea
                name="description"
                placeholder="Short presentation overview shown on the card"
                value={formData.description || ''}
                onChange={handleChange}
                className={`${inputClasses} h-24 resize-y`}
                required
              />
              <textarea
                name="spotlightStory"
                placeholder="Spotlight Story (Optional - leave blank if this presentation has no story yet)"
                value={formData.spotlightStory || ''}
                onChange={handleChange}
                className={`${inputClasses} h-28 resize-y`}
              />
              <p className="-mt-2 mb-4 text-sm text-gray-500">
                Leave Spotlight Story blank to show “Spotlight Story Not Available” for this presentation.
              </p>
            </>
          )}

          {itemType === 'testimonials' && (
            <textarea name="quote" placeholder="Testimonial Quote" value={formData.quote || ''} onChange={handleChange} className={`${inputClasses} h-24 resize-y`} required />
          )}

          {itemType === 'resources' && (
            <>
              <input type="text" name="name" placeholder="Resource Name" value={formData.name || ''} onChange={handleChange} className={inputClasses} required />
              <input type="url" name="link" placeholder="Resource Link (URL)" value={formData.link || ''} onChange={handleChange} className={inputClasses} required />
              <input type="url" name="thumbnail" placeholder="Resource Thumbnail URL (Optional)" value={formData.thumbnail || ''} onChange={handleChange} className={inputClasses} />
              <textarea name="description" placeholder="Resource Description" value={formData.description || ''} onChange={handleChange} className={`${inputClasses} h-24 resize-y`} required />
            </>
          )}

          {itemType === 'bulletins' && (
            <>
              <input type="text" name="what" placeholder="What" value={formData.what || ''} onChange={handleChange} className={inputClasses} required />
              <input type="text" name="when" placeholder="When" value={formData.when || ''} onChange={handleChange} className={inputClasses} required />
              <input type="text" name="where" placeholder="Where" value={formData.where || ''} onChange={handleChange} className={inputClasses} required />
              <input type="text" name="who" placeholder="Who" value={formData.who || ''} onChange={handleChange} className={inputClasses} required />
              <input type="url" name="image" placeholder="Image URL (Optional)" value={formData.image || ''} onChange={handleChange} className={inputClasses} />
              <input type="date" name="displayUntil" placeholder="Display Until (YYYY-MM-DD)" value={formData.displayUntil || ''} onChange={handleChange} className={inputClasses} required />
            </>
          )}

          <div className="flex justify-end space-x-4 mt-4">
            <button type="button" onClick={onClose} className="px-6 py-3 bg-gray-300 text-gray-800 font-semibold rounded-lg shadow-md hover:bg-gray-400 transition-all duration-300">Cancel</button>
            <button type="submit" className={buttonClasses} disabled={presentationSubmitDisabled}>{isNew ? 'Add' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ConfirmationModal = ({ message, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col overflow-hidden">
      <div className="p-4 bg-red-600 text-white flex justify-between items-center rounded-t-2xl">
        <h3 className="text-xl font-bold">Confirm Action</h3>
        <button onClick={onCancel} className="text-white hover:text-gray-200 text-2xl font-bold">×</button>
      </div>
      <div className="p-6 text-gray-800">
        <p className="mb-6">{message}</p>
        <div className="flex justify-end space-x-4">
          <button type="button" onClick={onCancel} className="px-6 py-3 bg-gray-300 text-gray-800 font-semibold rounded-lg shadow-md hover:bg-gray-400 transition-all duration-300">Cancel</button>
          <button type="button" onClick={onConfirm} className="px-6 py-3 bg-red-500 text-white font-bold rounded-lg shadow-md hover:bg-red-600 transition-all duration-300">Confirm</button>
        </div>
      </div>
    </div>
  </div>
);

const App = () => {
  const [dataLoadStatus, setDataLoadStatus] = useState('Loading data...');
  const [currentTab, setCurrentTab] = useState('home');
  const [presenters, setPresenters] = useState([]);
  const [presentations, setPresentations] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [resources, setResources] = useState([]);
  const [bulletins, setBulletins] = useState([]);
  const [selectedPresentation, setSelectedPresentation] = useState(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminAuthStatus, setAdminAuthStatus] = useState('Checking admin status...');
  const [authError, setAuthError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchData = async () => {
    try {
      const responses = await Promise.allSettled([
        fetch(`${API_BASE_URL}/presenters`),
        fetch(`${API_BASE_URL}/presentations`),
        fetch(`${API_BASE_URL}/testimonials`),
        fetch(`${API_BASE_URL}/resources`),
        fetch(`${API_BASE_URL}/bulletins`),
      ]);

      const processResponse = async (result, collectionName) => {
        if (result.status === 'fulfilled' && result.value.ok) {
          try {
            const data = await result.value.json();
            if (Array.isArray(data)) return data;
            console.error(`Backend /${collectionName} returned non-array data:`, data);
            return [];
          } catch (jsonError) {
            console.error(`Error parsing JSON for /${collectionName}:`, jsonError);
            return [];
          }
        }

        const errorDetail = result.status === 'rejected' ? result.reason : `HTTP ${result.value.status} ${result.value.statusText}`;
        console.error(`[Frontend Fetch] Failed to fetch ${collectionName}:`, errorDetail);
        return [];
      };

      const [presentersData, presentationsData, testimonialsData, resourcesData, bulletinsData] = await Promise.all([
        processResponse(responses[0], 'presenters'),
        processResponse(responses[1], 'presentations'),
        processResponse(responses[2], 'testimonials'),
        processResponse(responses[3], 'resources'),
        processResponse(responses[4], 'bulletins'),
      ]);

      setPresenters(presentersData);
      setPresentations(presentationsData);
      setTestimonials(testimonialsData);
      setResources(resourcesData);
      setBulletins(bulletinsData);
      setDataLoadStatus('Data loaded from backend.');
    } catch (error) {
      console.error('[Frontend Fetch] Critical error in fetchData:', error);
      setDataLoadStatus('Failed to load data from backend. Using mock data as fallback.');
      setPresenters(initialPresenters);
      setPresentations(initialPresentations);
      setTestimonials(initialTestimonials);
      setResources(initialResources);
      setBulletins(initialBulletins);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDomain = user.email?.split('@')[1];
        const authorizedDomains = ['findme.com.ph'];

        if (authorizedDomains.includes(userDomain)) {
          setCurrentUser(user);
          setAuthError('');
          try {
            const idToken = await user.getIdToken();
            const response = await fetch(`${API_BASE_URL}/verify_admin`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${idToken}`,
              },
              body: JSON.stringify({ idToken }),
            });
            const result = await response.json();
            if (response.ok && result.is_admin) {
              setIsAdmin(true);
              setAdminAuthStatus(`Logged in as admin: ${user.email}`);
            } else {
              setIsAdmin(false);
              setAdminAuthStatus(`Access denied: ${result.message || 'Not an authorized admin.'}`);
            }
          } catch (error) {
            console.error('Error verifying admin status with backend:', error);
            setIsAdmin(false);
            setAdminAuthStatus('Error verifying admin status.');
          }
        } else {
          setAuthError('Access denied. Please sign in with an authorized domain.');
          signOut(auth);
        }
      } else {
        setCurrentUser(null);
        setIsAdmin(false);
        setAdminAuthStatus('Not logged in.');
      }
    });

    const interval = setInterval(async () => {
      if (auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
        } catch (error) {
          console.error('Session expired. Signing out.', error);
          handleSignOut();
        }
      }
    }, 30 * 60 * 1000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const hydratedPresentations = React.useMemo(() => {
    return (presentations || []).map((presentation) => ({
      ...presentation,
      presenter: resolvePresenterForPresentation(presentation, presenters || []),
    }));
  }, [presentations, presenters]);

  const handleSignIn = async () => {
    try {
      setAuthError('');
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Error during Google Sign-in:', error);
      setAdminAuthStatus(`Sign-in failed: ${error.message}`);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setCurrentTab('home');
    } catch (error) {
      console.error('Error during sign-out:', error);
    }
  };

  const getIdToken = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('Authentication required.');
    return user.getIdToken();
  };

  const authorizedRequest = async (url, options = {}, fallbackMessage = 'Request failed') => {
    const idToken = await getIdToken();
    const response = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${idToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(await getApiError(response, fallbackMessage));
    }

    try {
      return await response.json();
    } catch {
      return {};
    }
  };

  const addPresenter = async (newPresenter) => {
    await authorizedRequest(`${API_BASE_URL}/presenters`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newPresenter),
    }, 'Failed to add presenter');
    await fetchData();
  };

  const updatePresenter = async (id, updatedPresenter) => {
    await authorizedRequest(`${API_BASE_URL}/presenters/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedPresenter),
    }, 'Failed to update presenter');
    await fetchData();
  };

  const deletePresenter = async (id) => {
    await authorizedRequest(`${API_BASE_URL}/presenters/${id}`, { method: 'DELETE' }, 'Failed to delete presenter');
    await fetchData();
  };

  const addPresentation = async (newPresentation) => {
    await authorizedRequest(`${API_BASE_URL}/presentations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newPresentation),
    }, 'Failed to add presentation');
    await fetchData();
  };

  const updatePresentation = async (id, updatedPresentation) => {
    await authorizedRequest(`${API_BASE_URL}/presentations/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedPresentation),
    }, 'Failed to update presentation');
    await fetchData();
  };

  const deletePresentation = async (id) => {
    await authorizedRequest(`${API_BASE_URL}/presentations/${id}`, { method: 'DELETE' }, 'Failed to delete presentation');
    await fetchData();
  };

  const addTestimonial = async (newTestimonial) => {
    await authorizedRequest(`${API_BASE_URL}/testimonials`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newTestimonial),
    }, 'Failed to add testimonial');
    await fetchData();
  };

  const updateTestimonial = async (id, updatedTestimonial) => {
    await authorizedRequest(`${API_BASE_URL}/testimonials/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedTestimonial),
    }, 'Failed to update testimonial');
    await fetchData();
  };

  const deleteTestimonial = async (id) => {
    await authorizedRequest(`${API_BASE_URL}/testimonials/${id}`, { method: 'DELETE' }, 'Failed to delete testimonial');
    await fetchData();
  };

  const addResource = async (newResource) => {
    await authorizedRequest(`${API_BASE_URL}/resources`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newResource),
    }, 'Failed to add resource');
    await fetchData();
  };

  const updateResource = async (id, updatedResource) => {
    await authorizedRequest(`${API_BASE_URL}/resources/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedResource),
    }, 'Failed to update resource');
    await fetchData();
  };

  const deleteResource = async (id) => {
    await authorizedRequest(`${API_BASE_URL}/resources/${id}`, { method: 'DELETE' }, 'Failed to delete resource');
    await fetchData();
  };

  const addBulletin = async (newBulletin) => {
    await authorizedRequest(`${API_BASE_URL}/bulletins`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newBulletin),
    }, 'Failed to add bulletin');
    await fetchData();
  };

  const updateBulletin = async (id, updatedBulletin) => {
    await authorizedRequest(`${API_BASE_URL}/bulletins/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedBulletin),
    }, 'Failed to update bulletin');
    await fetchData();
  };

  const deleteBulletin = async (id) => {
    await authorizedRequest(`${API_BASE_URL}/bulletins/${id}`, { method: 'DELETE' }, 'Failed to delete bulletin');
    await fetchData();
  };

  const handleViewSpotlightStory = (presentation) => {
    const hydratedPresentation = {
      ...presentation,
      presenter: resolvePresenterForPresentation(presentation, presenters || []),
    };
    setSelectedPresentation(hydratedPresentation);
    setCurrentTab('spotlight-story');
  };

  const renderContent = () => {
    switch (currentTab) {
      case 'home':
        return <Home onTalkToPrisma={() => setShowChatModal(true)} isLoadingData={dataLoadStatus === 'Loading data...'} />;
      case 'presentations':
        return <PresentationsView presenters={presenters} presentations={hydratedPresentations} onViewSpotlightStory={handleViewSpotlightStory} />;
      case 'testimonials':
        return <TestimonialsView testimonials={testimonials} />;
      case 'resources':
        return <ResourcesView resources={resources} />;
      case 'bulletins':
        return <BulletinsView bulletins={bulletins} />;
      case 'admin':
        if (adminAuthStatus === 'Checking admin status...') return <div className="text-center py-20">Checking admin status...</div>;
        if (isAdmin) {
          return (
            <AdminPanel
              presenters={presenters}
              presentations={presentations}
              testimonials={testimonials}
              resources={resources}
              bulletins={bulletins}
              addPresenter={addPresenter}
              updatePresenter={updatePresenter}
              deletePresenter={deletePresenter}
              addPresentation={addPresentation}
              updatePresentation={updatePresentation}
              deletePresentation={deletePresentation}
              addTestimonial={addTestimonial}
              updateTestimonial={updateTestimonial}
              deleteTestimonial={deleteTestimonial}
              addResource={addResource}
              updateResource={updateResource}
              deleteResource={deleteResource}
              addBulletin={addBulletin}
              updateBulletin={updateBulletin}
              deleteBulletin={deleteBulletin}
            />
          );
        }
        return (
          <div className="text-center py-20">
            <h2 className="text-4xl font-bold text-red-600 mb-4">Access Denied</h2>
            <p className="text-gray-700">You do not have administrative privileges to view this page.</p>
            <p className="text-gray-500 mt-2">Please sign in with an authorized admin account.</p>
            {currentUser ? <p className="text-sm text-gray-600 mt-2">Logged in as: {currentUser.email}</p> : (
              <button onClick={handleSignIn} className="mt-6 px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold text-xl rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-blue-300">
                Sign In with Google
              </button>
            )}
            <p className="text-sm text-gray-600 mt-4">{adminAuthStatus}</p>
          </div>
        );
      case 'spotlight-story':
        return <SpotlightStoryPage presentation={selectedPresentation} onBack={() => setCurrentTab('presentations')} />;
      default:
        return <Home onTalkToPrisma={() => setShowChatModal(true)} isLoadingData={dataLoadStatus === 'Loading data...'} />;
    }
  };

  const navButton = (tabKey, label, Icon) => (
    <li>
      <button
        onClick={() => setCurrentTab(tabKey)}
        className={`px-4 py-2 rounded-full text-lg font-semibold transition-all duration-300 flex items-center space-x-2 ${currentTab === tabKey ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' : 'text-gray-700 hover:bg-gray-100 hover:text-purple-600'}`}
      >
        <Icon />
        <span>{label}</span>
      </button>
    </li>
  );

  const mobileNavButton = (tabKey, label, Icon) => (
    <li>
      <button
        onClick={() => { setCurrentTab(tabKey); setMobileMenuOpen(false); }}
        className={`w-full px-4 py-2 rounded-full text-lg font-semibold transition-all duration-300 flex items-center justify-center space-x-2 ${currentTab === tabKey ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg' : 'text-gray-700 hover:bg-gray-100 hover:text-purple-600'}`}
      >
        <Icon />
        <span>{label}</span>
      </button>
    </li>
  );

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100 font-sans text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-8">Welcome to Prisma Archives</h1>
          <button onClick={handleSignIn} className="px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold text-xl rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-blue-300">
            Sign In with Google
          </button>
          {authError && <p className="text-red-500 mt-4">{authError}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100 font-sans text-gray-900">
      <script src="https://cdn.tailwindcss.com" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap" rel="stylesheet" />
      <style>{`
        body { font-family: 'Inter', sans-serif; }
        .animate-pulse-slow { animation: pulse-slow 4s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
        @keyframes pulse-slow { 0%, 100% { opacity: 1; } 50% { opacity: .7; } }
        .line-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
        .chat-message-content { font-size: 0.95rem; line-height: 1.65; }
        .chat-message-content p { margin: 0 0 0.55rem; }
        .chat-message-content p:last-child { margin-bottom: 0; }
        .chat-message-content ol, .chat-message-content ul { margin: 0.35rem 0 0; padding-left: 1.25rem; }
        .chat-message-content ol { list-style-type: decimal; }
        .chat-message-content ul { list-style-type: disc; }
        .chat-message-content li { margin: 0 0 0.45rem; padding-left: 0.15rem; }
        .chat-message-content li:last-child { margin-bottom: 0; }
        .chat-message-content strong { font-weight: 800; color: #111827; }
        .chat-message-content em { font-style: italic; }
      `}</style>

      <nav className="bg-white bg-opacity-80 backdrop-blur-sm shadow-lg p-4 md:p-5 flex justify-between items-center sticky top-0 z-50 rounded-b-xl">
        <div className="text-xl font-bold text-purple-700 md:hidden">Menu</div>
        <button className="md:hidden p-2 text-gray-700 hover:text-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-400 rounded-md" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
        </button>

        <ul className="hidden md:flex md:flex-row md:space-x-8">
          {navButton('home', 'Home', HomeIcon)}
          {navButton('bulletins', 'Bulletins', BulletinIcon)}
          {navButton('presentations', 'Presentations', PresentationIcon)}
          {navButton('testimonials', 'Testimonials', TestimonialIcon)}
          {navButton('resources', 'Resources', ResourceIcon)}
          {isAdmin && navButton('admin', 'Admin', AdminIcon)}
        </ul>

        {mobileMenuOpen && (
          <ul className="md:hidden absolute top-full left-0 w-full bg-white bg-opacity-95 shadow-lg flex flex-col items-center py-4 space-y-4 rounded-b-xl z-40">
            {mobileNavButton('home', 'Home', HomeIcon)}
            {mobileNavButton('bulletins', 'Bulletins', BulletinIcon)}
            {mobileNavButton('presentations', 'Presentations', PresentationIcon)}
            {mobileNavButton('testimonials', 'Testimonials', TestimonialIcon)}
            {mobileNavButton('resources', 'Resources', ResourceIcon)}
            {isAdmin && mobileNavButton('admin', 'Admin', AdminIcon)}
          </ul>
        )}
      </nav>

      <main className="container mx-auto py-8">{renderContent()}</main>
      {showChatModal && <PrismaChatbot isOpen={showChatModal} onClose={() => setShowChatModal(false)} />}
    </div>
  );
};

export default App;
